import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Textarea,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';

import { parseGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { Form as CustomForm } from 'src/components/common/form/Form';
import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { UserPermission } from 'src/lib/graphqlTypes/types';

import { CollectionPermissionsSection } from '../CollectionPermissionsSection';
import {
  UseCollectionNameValidationCollectionFragment,
  useUpsertCollectionValidation,
} from '../sharedValidation';
import { useSetMinEditRole } from '../useUpdateEditRoles';

export const ConfigureCollectionModal = ({
  collectionId,
  isOpen,
  onClose,
}: {
  collectionId: string;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { data, loading } = useQuery<
    GraphQLTypes.ConfigureCollectionEntryModalQuery,
    GraphQLTypes.ConfigureCollectionEntryModalQueryVariables
  >(
    gql`
      query ConfigureCollectionEntryModalQuery($collectionId: ID!) {
        operationCollection(id: $collectionId) {
          ... on OperationCollection {
            id
            name
            isShared
            isSandbox
            description
            minEditRole
            variants {
              id
            }
            ...UseCollectionNameValidationCollectionFragment
          }
        }
      }
      ${UseCollectionNameValidationCollectionFragment}
    `,
    { variables: { collectionId } },
  );

  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });

  const collection =
    data?.operationCollection.__typename === 'OperationCollection'
      ? data.operationCollection
      : undefined;
  const collectionGraphRef = parseGraphRef(collection?.variants[0]?.id);
  const isEnterprise = currentPlan.currentPlan?.userRoles === true;

  const [globalErrorMessage, setGlobalErrorMessage] = React.useState<string>();

  const { setMinEditRole } = useSetMinEditRole(setGlobalErrorMessage);

  const [updateCollectionName] = useMutation<
    GraphQLTypes.UpdateCollectionName,
    GraphQLTypes.UpdateCollectionNameVariables
  >(
    gql`
      mutation UpdateCollectionName($collectionId: ID!, $name: String!) {
        operationCollection(id: $collectionId) {
          updateName(name: $name) {
            ... on OperationCollection {
              id
              name
            }
            ... on Error {
              message
            }
          }
        }
      }
    `,
    { onError: (error) => setGlobalErrorMessage(error.message) },
  );
  const [updateCollectionDescription] = useMutation<
    GraphQLTypes.UpdateCollectionDescription,
    GraphQLTypes.UpdateCollectionDescriptionVariables
  >(
    gql`
      mutation UpdateCollectionDescription(
        $collectionId: ID!
        $description: String
      ) {
        operationCollection(id: $collectionId) {
          updateDescription(description: $description) {
            ... on OperationCollection {
              id
              description
            }
            ... on Error {
              message
            }
          }
        }
      }
    `,
    { onError: (error) => setGlobalErrorMessage(error.message) },
  );

  const { validationSchema, loading: upsertCollectionValidationLoading } =
    useUpsertCollectionValidation({ collection, loading });
  const initialValues = React.useMemo(
    (): Yup.InferType<typeof validationSchema> => ({
      name: collection?.name ?? '',
      description: collection?.description ?? '',
      minEditRole: collection?.minEditRole ?? null,
    }),
    [collection?.name, collection?.description, collection?.minEditRole],
  );

  const trackCustomEvent = useTrackCustomEvent();

  const {
    errors,
    touched,
    values,
    setFieldValue,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
  } = useFormik({
    initialValues,
    enableReinitialize: true,
    async onSubmit(formValues) {
      try {
        const updateNameSuccessfulPromise =
          initialValues.name === formValues.name
            ? true
            : updateCollectionName({
                variables: { collectionId, name: formValues.name },
              }).then(({ data: updateNameData }) => {
                const result = updateNameData?.operationCollection?.updateName;
                if (result?.__typename === 'OperationCollection') {
                  return true;
                }
                setGlobalErrorMessage(
                  result?.message ?? 'Failed to update name',
                );
                return false;
              });
        const updateDescriptionSuccessfulPromise =
          initialValues.description === formValues.description
            ? true
            : updateCollectionDescription({
                variables: {
                  collectionId,
                  description: formValues.description,
                },
              }).then(({ data: updateDescriptionData }) => {
                const result =
                  updateDescriptionData?.operationCollection?.updateDescription;
                if (result?.__typename === 'OperationCollection') {
                  return true;
                }
                setGlobalErrorMessage(
                  result?.message ?? 'Failed to update description',
                );
                return false;
              });

        const updateEditRolesPromise =
          !collection?.isShared ||
          initialValues.minEditRole === formValues.minEditRole
            ? true
            : setMinEditRole({
                collectionId,
                minEditRole: values.minEditRole,
              }).then(({ data: updateDescriptionData }) => {
                const result =
                  updateDescriptionData?.operationCollection?.setMinEditRole;
                if (result?.__typename === 'OperationCollection') {
                  return true;
                }
                setGlobalErrorMessage(
                  result?.message ?? 'Failed to set min edit role',
                );
                return false;
              });

        const updateNameSuccessful = await updateNameSuccessfulPromise;
        const updateDescriptionSuccessful =
          await updateDescriptionSuccessfulPromise;
        const updateEditRolesSuccessful = await updateEditRolesPromise;
        if (
          updateNameSuccessful &&
          updateDescriptionSuccessful &&
          updateEditRolesSuccessful
        ) {
          trackCustomEvent({
            action: 'configure_operation_collection',
            category: 'Operation Collections',
            label: collection?.isSandbox
              ? 'sandbox'
              : collection?.isShared
                ? 'shared'
                : 'personal',
          });
          onClose();
        }
      } catch {
        // Surfaced through `createCollectionError`
      }
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: true,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <CustomForm onSubmit={handleSubmit} noValidate={true}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {collection
                ? `Configure ${
                    collection.isShared ? 'Shared' : 'Personal'
                  } Collection`
                : 'Configure Collection'}
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            {upsertCollectionValidationLoading ||
            loading ||
            currentPlan.loading ? (
              <Loading />
            ) : !collection ? (
              <div>Could not fetch details for collection</div>
            ) : (
              <>
                <FormControl
                  isRequired
                  isInvalid={!!errors.name && !!touched.name}
                >
                  <FormLabel>Collection Name</FormLabel>
                  <Input
                    autoFocus
                    name="name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={`Your ${
                      collection.isShared ? 'Shared' : 'Personal'
                    } Collection`}
                    value={values.name}
                    isDisabled={isSubmitting}
                  />
                  <FormErrorMessage>{errors.name}</FormErrorMessage>
                </FormControl>
                <FormControl
                  isInvalid={!!errors.description && !!touched.description}
                >
                  <FormLabel>Collection Description</FormLabel>
                  <Textarea
                    name="description"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.description ?? undefined}
                    isDisabled={isSubmitting}
                  />
                  <FormErrorMessage>{errors.description}</FormErrorMessage>
                  <FormHelperText>
                    {values.description?.length ?? 0}/1000 characters used
                  </FormHelperText>
                </FormControl>
                {collection.isShared && collectionGraphRef && (
                  <CollectionPermissionsSection
                    graphRef={collectionGraphRef}
                    isEnterprise={isEnterprise}
                    minEditRole={values.minEditRole}
                    setMinEditRole={(minEditRole: UserPermission | null) =>
                      setFieldValue('minEditRole', minEditRole)
                    }
                  />
                )}
                {globalErrorMessage && (
                  <ErrorMessage>{globalErrorMessage}</ErrorMessage>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                variant="secondary"
                onClick={onClose}
                type="button"
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
              <Tooltip
                disabled={Object.entries(errors).length === 0}
                label={Object.values(errors).flatMap((error, i) => (
                  <>
                    {i !== 0 && <br />}
                    {error}
                  </>
                ))}
              >
                <Button
                  variant="primary"
                  isDisabled={
                    upsertCollectionValidationLoading ||
                    !collection ||
                    Object.entries(errors).length !== 0
                  }
                  isLoading={isSubmitting}
                  loadingText="Confirm"
                  type="submit"
                >
                  Confirm
                </Button>
              </Tooltip>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </CustomForm>
    </Modal>
  );
};
